/**
 * PHASE 5: useOfflineAction Hook
 * Hook for making any action offline-capable with a single call
 * 
 * Usage:
 * const { execute, isExecuting, lastResult } = useOfflineAction({
 *   type: 'AVAILABILITY_UPDATE',
 *   endpoint: '/api/availability/me',
 *   method: 'PUT'
 * });
 * 
 * // Later in code:
 * const result = await execute({ payload: availabilityData });
 */

import { useState, useCallback } from 'react';
import { offlineCapableFetch } from '@/lib/offline';
import { showQueuedToast, showSyncedActionToast, showFailedActionToast } from '@/components/offline';

/**
 * @typedef {Object} UseOfflineActionOptions
 * @property {string} type - Action type for queue (e.g., 'TIMECLOCK_PUNCH')
 * @property {string} endpoint - Default API endpoint
 * @property {string} [method='POST'] - HTTP method
 * @property {number} [priority=5] - Queue priority
 * @property {boolean} [requiresAuth=true] - Whether auth is required
 * @property {boolean} [showToasts=true] - Show toast notifications
 * @property {string} [successMessage] - Custom success message
 * @property {string} [offlineMessage] - Custom offline message
 * @property {string} [entityType] - Entity type for deduplication
 */

/**
 * Hook to make any action offline-capable
 * @param {UseOfflineActionOptions} options
 */
export function useOfflineAction(options) {
  const {
    type,
    endpoint: defaultEndpoint,
    method = 'POST',
    priority = 5,
    requiresAuth = true,
    showToasts = true,
    successMessage,
    offlineMessage,
    entityType
  } = options;

  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  /**
   * Execute the action with offline support
   * @param {Object} params - { payload, endpoint?, entityId?, userId?, merchantId? }
   */
  const execute = useCallback(async (params = {}) => {
    const {
      payload,
      endpoint = defaultEndpoint,
      entityId,
      userId,
      merchantId,
      ...extraOptions
    } = params;

    setIsExecuting(true);

    try {
      const result = await offlineCapableFetch(
        endpoint,
        {
          method,
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json'
          }
        },
        {
          type,
          entityType,
          entityId,
          userId,
          merchantId,
          priority,
          requiresAuth,
          successMessage,
          offlineMessage: offlineMessage || 'Saved offline. Will sync when online.',
          ...extraOptions
        }
      );

      setLastResult(result);

      // Show appropriate toast
      if (showToasts) {
        if (result.queued) {
          showQueuedToast(offlineMessage);
        } else if (result.ok) {
          showSyncedActionToast(type);
        } else if (!result.ok) {
          showFailedActionToast(type, result.error);
        }
      }

      return result;

    } catch (error) {
      const errorResult = {
        ok: false,
        error: error.message
      };
      setLastResult(errorResult);

      if (showToasts) {
        showFailedActionToast(type, error.message);
      }

      return errorResult;
    } finally {
      setIsExecuting(false);
    }
  }, [defaultEndpoint, method, type, entityType, priority, requiresAuth, showToasts, successMessage, offlineMessage]);

  return {
    execute,
    isExecuting,
    lastResult
  };
}

/**
 * The signed-in staff member's own punch (labor-service POST /api/timeclock/v2/punch with a
 * client punch id). Offline — or when the answer is lost — the punch is kept on the device
 * with the same id and time (lib/timeclock/punchQueue) and replayed idempotently.
 * execute({ action: 'CLOCK_IN' | 'BREAK_START' | 'BREAK_END' | 'CLOCK_OUT', break_paid, role_id })
 * -> { success, queued, data, error }
 */
export function useTimeclockAction() {
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const execute = useCallback(async (body = {}) => {
    const { timeClockApi } = await import('@/lib/timeclock/api');
    const { queuePunch, newClientPunchId, deviceId, sessionFor } = await import('@/lib/timeclock/punchQueue');
    const session = sessionFor('staff');
    if (!session) {
      const result = { success: false, queued: false, error: 'Sign in as a staff member to use your time clock' };
      setLastResult(result);
      return result;
    }
    setIsExecuting(true);
    const clientPunchId = newClientPunchId();
    const at = new Date();
    try {
      const data = await timeClockApi(session.headers).punch({ ...body, client_punch_id: clientPunchId, device_id: deviceId() });
      const result = { success: true, queued: false, data };
      setLastResult(result);
      return result;
    } catch (e) {
      if (!e.network) {
        const result = { success: false, queued: false, error: e.message, code: e.code, detail: e.detail };
        setLastResult(result);
        return result;
      }
      await queuePunch({ auth: 'staff', restaurantId: session.restaurantId, who: { staffId: session.staffId },
                         action: String(body.action || '').toUpperCase(), breakPaid: !!body.break_paid,
                         roleId: body.role_id || null, clientPunchId, at });
      const result = { success: true, queued: true };
      setLastResult(result);
      return result;
    } finally {
      setIsExecuting(false);
    }
  }, []);
  return { execute, isExecuting, lastResult };
}

/**
 * Pre-configured hook for availability updates
 */
export function useAvailabilityAction() {
  return useOfflineAction({
    type: 'AVAILABILITY_UPDATE',
    endpoint: '/api/availability/me',
    method: 'PUT',
    priority: 3,
    entityType: 'availability',
    successMessage: 'Availability updated',
    offlineMessage: 'Availability saved. Will sync when online.'
  });
}

/**
 * Pre-configured hook for schedule acknowledgements
 */
export function useScheduleAckAction() {
  return useOfflineAction({
    type: 'SCHEDULE_ACK',
    endpoint: '/api/labor/shifts/acknowledge',
    method: 'POST',
    priority: 2,
    entityType: 'shift',
    successMessage: 'Shift acknowledgment recorded',
    offlineMessage: 'Acknowledgment saved. Will sync when online.'
  });
}

export default useOfflineAction;
